
/* eslint-disable  jsx-a11y/label-has-associated-control */

import React, { useMemo, useRef, useState } from "react";
import "./EvaluatorReportFilter.css";
import {
  MAX_DATE_RANGE_MONTHS,
  MIN_REPORT_DATE,
  MIN_REPORT_DATE_LABEL,
  addMonths,
  getEndDateAfterStartChange,
  getMaxEndDate,
  getTodayDate,
  isValidDateValue,
  validateDateRange
} from "./reportDateUtils";

const REQUIRED_DATES_MESSAGE = "Start Date and End Date are mandatory.";

/**
 * Default date range:
 * Start Date = 12 calendar months before today
 *              (never earlier than 01/01/2020)
 * End Date = today
 */
const getDefaultDates = () => {
  const endDate = getTodayDate();
  const startDate = addMonths(endDate, -MAX_DATE_RANGE_MONTHS);

  return {
    startDate:
      startDate < MIN_REPORT_DATE ? MIN_REPORT_DATE : startDate,
    endDate,
  };
};

const EvaluatorReportFilter = ({
  onDownload,
  isLoading = false,
}) => {
  const defaultDates = useMemo(() => getDefaultDates(), []);

  const [startDate, setStartDate] = useState(defaultDates.startDate);

  const [endDate, setEndDate] = useState(defaultDates.endDate);

  /*
   * Error from the last Download attempt.
   */
  const [error, setError] = useState("");

  /*
   * Name of the date input currently being edited.
   * Date errors are hidden while the user is typing.
   */
  const [activeDateField, setActiveDateField] = useState("");

  const startDateOnFocusRef = useRef("");

  /**
   * Today's date in YYYY-MM-DD format.
   * Used to prevent selecting future dates.
   */
  const today = getTodayDate();

  const isStartDateValid = isValidDateValue(startDate, today);

  /**
   * Maximum End Date allowed:
   * earlier of Start Date + 12 months and today.
   */
  const maxEndDate = getMaxEndDate(startDate, today);

  const dateRangeError = validateDateRange(startDate, endDate, {
    isDateRequired: true,
    requiredMessage: REQUIRED_DATES_MESSAGE,
    todayDate: today,
  });

  /**
   * Store exactly what the native date input reports.
   *
   * While typing, the input can report "" or partial
   * years such as "0002-05-12". Those values are never
   * rewritten here, because writing a different value
   * back into a controlled date input resets what the
   * user is typing.
   */
  const handleStartDateChange = (event) => {
    setError("");
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setError("");
    setEndDate(event.target.value);
  };

  const handleDateFocus = (event) => {
    const { name } = event.target;

    setActiveDateField(name);

    if (name === "startDate") {
      startDateOnFocusRef.current = startDate;
    }
  };

  /**
   * When Start Date editing is finished, clear End Date
   * only if the new Start Date makes it invalid.
   */
  const handleDateBlur = (event) => {
    setActiveDateField("");

    if (event.target.name !== "startDate") {
      return;
    }

    setEndDate(
      getEndDateAfterStartChange(
        startDateOnFocusRef.current,
        startDate,
        endDate,
        today
      )
    );
  };

  /**
   * Download report.
   */
  const handleDownload = async () => {
    /*
     * Final validation, using a fresh "today".
     */
    const validationError = validateDateRange(startDate, endDate, {
      isDateRequired: true,
      requiredMessage: REQUIRED_DATES_MESSAGE,
      todayDate: getTodayDate(),
    });

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");

    if (typeof onDownload !== "function") {
      return;
    }

    try {

      await onDownload({
        startDate,
        endDate,
      });
    } catch(err){
      console.log("Error :" , err )
    }
  };

  /**
   * Reset back to default date range.
   */
  const handleReset = () => {
    const resetDates = getDefaultDates();

    setStartDate(resetDates.startDate);
    setEndDate(resetDates.endDate);
    setError("");
  };

  const displayedError =
    error || (activeDateField ? "" : dateRangeError);

  const isDownloadDisabled =
    Boolean(dateRangeError) ||
    isLoading;

  return (
    <section
      className="evaluator-report-filter"
      aria-labelledby="evaluator-report-title"
    >
      <div className="vf-challenge-filter-header">
        <h2
          id="evaluator-report-title"
          className="vf-challenge-filter-title"
        >
          Evaluator Report Filters
        </h2>

        <p className="vf-challenge-filter-subtitle">
          Download Evaluator data based on date range.
        </p>
      </div>

      <div className="vf-evaluator-filter-grid">
        {/* Start Date */}
        <div className="vf-evaluator-filter-item">
          <label
            htmlFor="evaluator-start-date"
            className="vf-evaluator-filter-label"
          >
            <span className="vf-evaluator-filter-label-text">
              Start Date
              <span
                className="vf-required"
                aria-hidden="true"
              >
                *
              </span>

              <span
                className="icon-info tooltip_v"
                aria-hidden="true"
              >
                <span className="tooltiptext_v">
                  Select a start date from{" "}
                  {MIN_REPORT_DATE_LABEL} onwards. Maximum
                  allowed range is {MAX_DATE_RANGE_MONTHS} months.
                </span>
              </span>
            </span>
          </label>

            <input
              id="evaluator-start-date"
              name="startDate"
              type="date"
              value={startDate}
              min={MIN_REPORT_DATE}
              max={today} // Future dates disabled
              className={`vf-evaluator-filter-input ${
                displayedError ? "evaluator-report-filter__input--error" : ""
              }`}
              onChange={handleStartDateChange}
              onFocus={handleDateFocus}
              onBlur={handleDateBlur}
              aria-label="Start Date"
            />
        </div>

        {/* End Date */}
        <div className="vf-evaluator-filter-item">
          <label
            htmlFor="evaluator-end-date"
            className="vf-evaluator-filter-label"
          >
            <span className="vf-evaluator-filter-label-text">
              End Date
              <span
                className="vf-required"
                aria-hidden="true"
              >
                *
              </span>

              <span
                className="icon-info tooltip_v"
                aria-hidden="true"
              >
                <span className="tooltiptext_v">
                  End Date cannot be earlier than
                  Start Date and cannot exceed{" "}
                  {MAX_DATE_RANGE_MONTHS} months from
                  Start Date.
                </span>
              </span>
            </span>
          </label>

          <input
            id="evaluator-end-date"
            name="endDate"
            type="date"
            value={endDate}
            min={isStartDateValid ? startDate : MIN_REPORT_DATE}
            max={maxEndDate}
            disabled={!isStartDateValid}
            className={`vf-evaluator-filter-input ${
              displayedError
                ? "evaluator-report-filter__input--error"
                : ""
            }`}
            onChange={handleEndDateChange}
            onFocus={handleDateFocus}
            onBlur={handleDateBlur}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="vf-evaluator-filter-actions">
        <button
          type="button"
          className="vf-evaluator-report-button"
          onClick={handleDownload}
          disabled={isDownloadDisabled}
        >
          {isLoading
            ? "Downloading..."
            : "Download Evaluator Report"}
        </button>

        <button
          type="button"
          className="vf-btn1 vf-btn-secondaryReport"
          onClick={handleReset}
          disabled={isLoading}
        >
          Reset
        </button>
      </div>

      {/* Validation Message */}
      {displayedError && (
        <div
          id="evaluator-date-validation-message"
          className="evaluator-report-filter__error"
          role="alert"
        >
          <span
            className="evaluator-report-filter__error-icon"
            aria-hidden="true"
          >
            !
          </span>

          <span>{displayedError}</span>
        </div>
      )}
    </section>
  );
};

export default EvaluatorReportFilter;
